"use strict";
/*
 * © 2025 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GenericTreeFilter = GenericTreeFilter;
const react_1 = __importDefault(require("react"));
const react_i18next_1 = require("react-i18next");
const index_1 = require("../index");
const style_1 = require("./style");
// Default node renderer component
function DefaultNodeRenderer({ node, isSelected, selectionType, onToggle, translationNamespace, }) {
    const { t } = (0, react_i18next_1.useTranslation)(translationNamespace);
    const selectionTooltips = {
        none: t("message-Not selected"),
        all: t("message-Selected"),
        inner: t("message-selectedInner"),
        outer: t("message-selectedOuter"),
    };
    const inputId = "cbx_" + (0, index_1.uniqueID)();
    return (react_1.default.createElement(style_1.NodeWrapper, { className: "hover-highlight", onClick: onToggle, style: { cursor: "pointer" } },
        react_1.default.createElement(index_1.Flex, { width: "100%", justify: "space-between", align: "center" },
            react_1.default.createElement(index_1.Flex, { align: "center", justify: "flex-start", noGrow: true, noOverflow: true, noResize: true, noWrap: true, width: "80%" },
                react_1.default.createElement(index_1.TooltipOnOverflow, { style: {
                        flexGrow: 1,
                        width: 0,
                    } }, node.name)),
            react_1.default.createElement(index_1.FlexSpace, null),
            react_1.default.createElement(index_1.Checkbox, { id: inputId, title: selectionTooltips[selectionType], checked: isSelected, symbol: index_1.selectionSymbols[selectionType], name: node.name, onChange: () => { } }))));
}
// Simple reducer for managing expanded/collapsed state
function toggleReducer(openIds, id) {
    return openIds.includes(id)
        ? openIds.filter((openId) => openId !== id)
        : [...openIds, id];
}
/** Generic tree filter component that works with any hierarchical data */
function GenericTreeFilter({ nodes = [], selectedIds = [], onNodeToggle, getSelectionType = () => "none", renderNode, emptyMessage = "No items available", translationNamespace = "central-web-app-components", style, className, unassignedLabel, }) {
    const { t } = (0, react_i18next_1.useTranslation)(translationNamespace);
    const [openIds, toggleExpanded] = react_1.default.useReducer(toggleReducer, []);
    // Default unassigned label
    const defaultUnassignedLabel = unassignedLabel || t("label-Unassigned Machines");
    const handleNodeToggle = react_1.default.useCallback((node) => {
        if (onNodeToggle) {
            onNodeToggle(node, nodes, selectedIds);
        }
    }, [onNodeToggle, nodes, selectedIds]);
    const renderTreeNode = react_1.default.useCallback(({ id }) => {
        const node = nodes.find((n) => n.id === id);
        if (!node)
            return null;
        const isSelected = selectedIds.includes(id);
        const selectionType = getSelectionType(id);
        const displayName = node.id ? node.name : defaultUnassignedLabel;
        const nodeWithDisplayName = { ...node, name: displayName };
        const nodeProps = {
            node: nodeWithDisplayName,
            isSelected,
            selectionType,
            onToggle: () => handleNodeToggle(node),
            translationNamespace,
        };
        if (renderNode) {
            return renderNode(nodeProps);
        }
        return react_1.default.createElement(DefaultNodeRenderer, { ...nodeProps });
    }, [
        nodes,
        selectedIds,
        getSelectionType,
        handleNodeToggle,
        renderNode,
        translationNamespace,
        defaultUnassignedLabel,
    ]);
    const getChildren = react_1.default.useCallback((id) => {
        const node = nodes.find((n) => n.id === id);
        if (!(node === null || node === void 0 ? void 0 : node.children))
            return [];
        return node.children
            .map((child) => typeof child === "string" ? nodes.find((n) => n.id === child) : child)
            .filter(Boolean);
    }, [nodes]);
    // Get root nodes (nodes without parents)
    const rootNodes = react_1.default.useMemo(() => {
        const unassignedNode = nodes.find((n) => n.id === "");
        const regularRoots = nodes.filter((n) => n.id && !n.parentId);
        return [unassignedNode, ...regularRoots].filter(Boolean);
    }, [nodes]);
    if (nodes.length === 0) {
        return (react_1.default.createElement("div", { style: style, className: className },
            react_1.default.createElement(index_1.EmptyState, { text: emptyMessage }),
            react_1.default.createElement(index_1.FlexSpace, { size: "gutter" })));
    }
    return (react_1.default.createElement(index_1.Flex, { col: true, noWrap: true, overflow: "auto" },
        rootNodes.map((node) => (react_1.default.createElement(index_1.TreeView, { key: node.id, id: node.id, render: renderTreeNode, getChildren: getChildren, isOpen: (id) => openIds.includes(id), onClick: (id) => toggleExpanded(id) }))),
        react_1.default.createElement(index_1.FlexSpace, { size: "gutter" })));
}
//# sourceMappingURL=GenericTreeview.js.map